import { useParams, Link } from 'react-router-dom';
import { useFetch } from '../hooks/useFetch.js';
import { useBrand } from '../hooks/useBrand.jsx';
import { useAuth } from '../hooks/useAuth.jsx';
import { Seo } from '../components/Seo.jsx';
import { BrandPage } from '../components/BrandPage.jsx';
import { Button, Spinner } from '../components/ui.jsx';

export default function BrandDetail() {
  const { slug } = useParams();
  const { data, error, loading, reload } = useFetch(`/brands/${slug}`);
  const brands = useBrand();
  const { user } = useAuth();

  if (loading && !data) {
    return (
      <div className="flex min-h-96 items-center justify-center text-ink-faint">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="text-2xl font-semibold">{error.message}</h1>
        <Button to="/programs" variant="outline" className="mt-6">
          Browse all programs
        </Button>
      </div>
    );
  }

  const { brand, programs, viewerIsOwner } = data;

  return (
    <>
      <Seo
        title={`${brand.companyName} — partnership programs`}
        description={`${brand.companyName} runs ${programs.length} public partnership program${programs.length === 1 ? '' : 's'} on Affily. Each program’s commission, cookie window and payout schedule is on its page.`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: brand.companyName,
          url: brand.website ?? undefined,
          logo: brand.logoUrl ?? undefined,
          description: brand.description ?? undefined,
        }}
      />

      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <nav className="text-sm text-ink-muted">
            <Link to="/programs" className="hover:text-ink">
              Programs
            </Link>
          </nav>
          {!viewerIsOwner && brand.acceptsPitches && (
            <PitchButton slug={brand.slug} signedIn={Boolean(user)} />
          )}
        </div>

        <BrandPage
          brand={brand}
          programs={programs}
          canEdit={viewerIsOwner}
          onSaved={async () => {
            // The switcher shows the name and logo too.
            await Promise.all([reload(), brands.reload().catch(() => {})]);
          }}
        />
      </div>
    </>
  );
}

/**
 * "Pitch a campaign": a creator suggests a campaign to this brand. Anyone not
 * signed in signs up as a creator first and lands on the form.
 */
function PitchButton({ slug, signedIn }) {
  const form = `/dashboard/creator/pitches/new?brand=${encodeURIComponent(slug)}`;
  return (
    <Button
      to={signedIn ? form : `/signup?role=creator&next=${encodeURIComponent(form)}`}
      variant="outline"
      size="sm"
    >
      Pitch a campaign
    </Button>
  );
}
